// Market Gap Scanner: analyse chart penetration across storefronts.
//
// Given the enriched row array (after chart enrichment), compute:
//   - How many countries the app is charting in per chart type (free/paid/grossing)
//   - Which major markets the app is NOT charting in at all
//
// All functions are pure so they can be unit tested without network access.

/**
 * Major markets by regional group, ordered by market significance.
 * Used to prioritise the "notable gaps" list.
 */
const MAJOR_MARKETS = [
  'us', 'gb', 'de', 'fr', 'jp', 'kr', 'au', 'ca', 'it', 'es',
  'nl', 'se', 'br', 'mx', 'in', 'sg', 'hk', 'tw', 'cn', 'ru',
  'sa', 'ae', 'za', 'ar', 'pl', 'tr',
];

/**
 * @typedef {object} ChartPenetration
 * @property {number} free    - count of countries with chartRankFree ≤ 100
 * @property {number} paid    - count of countries with chartRankPaid ≤ 100
 * @property {number} grossing - count of countries with chartRankGrossing ≤ 100
 * @property {number} any     - count of countries charting in at least one chart type
 * @property {number} total   - total available storefronts in the row set
 */

/**
 * Compute chart penetration counts from an array of enriched RegionRows.
 *
 * Rows without chart data (chartRankFree/Paid/Grossing all null) do not
 * count toward `any`, but are included in `total` if available.
 *
 * @param {Array<{countryCode: string, available?: boolean, chartRankFree?: number|null, chartRankPaid?: number|null, chartRankGrossing?: number|null}>} rows
 * @returns {ChartPenetration}
 */
export function chartPenetration(rows) {
  let free = 0, paid = 0, grossing = 0, any = 0, total = 0;
  for (const row of rows) {
    if (!row.countryCode || row.available === false) continue;
    total++;
    const inFree = row.chartRankFree != null && row.chartRankFree >= 1;
    const inPaid = row.chartRankPaid != null && row.chartRankPaid >= 1;
    const inGrossing = row.chartRankGrossing != null && row.chartRankGrossing >= 1;
    if (inFree) free++;
    if (inPaid) paid++;
    if (inGrossing) grossing++;
    if (inFree || inPaid || inGrossing) any++;
  }
  return { free, paid, grossing, any, total };
}

/**
 * @typedef {object} SnapshotPenetration
 * @property {number}   count          - number of snapshot countries whose chart contains the track id
 * @property {string[]} countries      - those storefront codes, lower-cased and sorted
 * @property {number}   totalCountries - how many countries the snapshot covers at all
 */

/**
 * Count, against a committed cross-country chart snapshot, how many of its
 * countries list `trackId` in their chart. Pure: reads only the passed snapshot,
 * never the network — so it works with the static build and no RSS proxy.
 *
 * The snapshot shape is either `{ countries: { <code>: entries[] } }` or the
 * Chart Browser sample shape `{ countries: { <code>: { <type>: entries[] } } }`.
 * Each entry is either a track id (string|number) or an object with a `.trackId`
 * field, so the same helper works for committed samples and live chart exports.
 * Track ids are compared as strings to bridge numeric/string ids.
 *
 * Callers that show a "also charts in N countries" hint should require
 * `totalCountries >= 2`; with only one country covered there is no cross-country
 * breadth to report and the hint should degrade silently.
 *
 * @param {string|number|null|undefined} trackId
 * @param {{ countries?: Record<string, Array<string|number|{trackId?: string|number}>|Record<string, Array<string|number|{trackId?: string|number}>>> }|null|undefined} snapshot
 * @returns {SnapshotPenetration}
 */
export function chartPenetrationFromSnapshot(trackId, snapshot) {
  const id = trackId == null ? '' : String(trackId);
  const byCountry = snapshot && typeof snapshot === 'object' ? snapshot.countries : null;
  if (!id || !byCountry || typeof byCountry !== 'object') {
    return { count: 0, countries: [], totalCountries: 0 };
  }

  const countries = [];
  let totalCountries = 0;
  for (const [code, countrySnapshot] of Object.entries(byCountry)) {
    const entries = snapshotEntriesForCountry(countrySnapshot);
    if (entries.length === 0) continue;
    totalCountries++;
    const present = entries.some((entry) => {
      const entryId = entry != null && typeof entry === 'object' ? entry.trackId : entry;
      return entryId != null && String(entryId) === id;
    });
    if (present) countries.push(String(code).toLowerCase());
  }
  countries.sort();
  return { count: countries.length, countries, totalCountries };
}

/**
 * @param {Array<string|number|{trackId?: string|number}>|Record<string, Array<string|number|{trackId?: string|number}>>|null|undefined} countrySnapshot
 * @returns {Array<string|number|{trackId?: string|number}>}
 */
function snapshotEntriesForCountry(countrySnapshot) {
  if (Array.isArray(countrySnapshot)) return countrySnapshot;
  if (!countrySnapshot || typeof countrySnapshot !== 'object') return [];

  return Object.values(countrySnapshot).flatMap((entries) => (
    Array.isArray(entries) ? entries : []
  ));
}

/**
 * Return up to `maxGaps` major-market country codes that are available in the
 * storefront list but NOT charting in any chart type.
 *
 * "Charting" means at least one of chartRankFree/Paid/Grossing is a positive number.
 * Markets where the app is unavailable are excluded from the gap list.
 *
 * @param {Array<{countryCode: string, available?: boolean, chartRankFree?: number|null, chartRankPaid?: number|null, chartRankGrossing?: number|null}>} rows
 * @param {number} [maxGaps=5]
 * @returns {string[]}
 */
export function unchartedMarkets(rows, maxGaps = 5) {
  const byCode = new Map();
  for (const row of rows) {
    if (row.countryCode) byCode.set(row.countryCode, row);
  }

  const gaps = [];
  for (const code of MAJOR_MARKETS) {
    if (gaps.length >= maxGaps) break;
    const row = byCode.get(code);
    if (!row || row.available === false) continue;
    const inFree = row.chartRankFree != null && row.chartRankFree >= 1;
    const inPaid = row.chartRankPaid != null && row.chartRankPaid >= 1;
    const inGrossing = row.chartRankGrossing != null && row.chartRankGrossing >= 1;
    if (!inFree && !inPaid && !inGrossing) {
      gaps.push(code);
    }
  }
  return gaps;
}
